// src/context/AlbumContext.js
import React, { createContext, useContext, useEffect, useState } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

const AlbumContext = createContext(undefined);

export function AlbumProvider({ user, children }) {
  const [privateAlbums, setPrivateAlbums] = useState([]);
  const [sharedAlbums, setSharedAlbums] = useState([]);
  const [currentAlbumId, setCurrentAlbumId] = useState(null);
  const [mode, setMode] = useState('private');
  const [loading, setLoading] = useState(true);

  // Load user's albums
  useEffect(() => {
    if (!user) {
      setPrivateAlbums([]);
      setSharedAlbums([]);
      setCurrentAlbumId(null);
      setLoading(false);
      return;
    }

    setLoading(true);

    // Query albums where user is a member
    const albumsRef = collection(db, 'albums');
    const q = query(albumsRef, where('members', 'array-contains', user.uid));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const albums = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Split into private and shared using the type field
      const privateList = albums.filter(a => a.type === 'PRIVATE');
      const sharedList = albums.filter(a => a.type === 'SHARED');

      setPrivateAlbums(privateList);
      setSharedAlbums(sharedList);

      // Auto-select album if none selected
      const savedAlbumId = window.localStorage.getItem('currentAlbumId');
      const savedMode = window.localStorage.getItem('currentAlbumMode') || 'private';

      if (savedAlbumId && albums.some(a => a.id === savedAlbumId)) {
        // Restore saved album
        setCurrentAlbumId(savedAlbumId);
        setMode(savedMode);
      } else if (!currentAlbumId) {
        // Auto-select first available album
        if (privateList[0]) {
          setMode('private');
          setCurrentAlbumId(privateList[0].id);
        } else if (sharedList[0]) {
          setMode('shared');
          setCurrentAlbumId(sharedList[0].id);
        }
      }

      setLoading(false);
    }, (error) => {
      console.error('Error listening to albums:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  const setPrivateAlbum = (albumId) => {
    setMode('private');
    setCurrentAlbumId(albumId);
    window.localStorage.setItem('currentAlbumMode', 'private');
    window.localStorage.setItem('currentAlbumId', albumId);
  };

  const setSharedAlbum = (albumId) => {
    setMode('shared');
    setCurrentAlbumId(albumId);
    window.localStorage.setItem('currentAlbumMode', 'shared');
    window.localStorage.setItem('currentAlbumId', albumId);
  };

  const value = {
    currentAlbumId,
    mode,
    privateAlbums,
    sharedAlbums,
    setPrivateAlbum,
    setSharedAlbum,
    loading,
  };

  return (
    <AlbumContext.Provider value={value}>
      {children}
    </AlbumContext.Provider>
  );
}

export function useAlbum() {
  const ctx = useContext(AlbumContext);
  if (!ctx) {
    throw new Error('useAlbum must be used inside AlbumProvider');
  }
  return ctx;
}

